import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/images/invertLogo.png';
import logoWhite from '../assets/images/invertLogoWhite.png';
import { useOpenDashboard } from '../hooks/useOpenDashboard';
import Button from './Button';

const NAV_LINKS = [
  { label: 'Product', to: '/product' },
  { label: 'For Restaurants', to: '/restaurant-tv' },
  { label: 'Applications', to: '/applications' },
  { label: 'Network', to: '/network' },
  { label: 'Proof', to: '/proof' },
  { label: 'Pricing', to: '/pricing' },
];

const ADVERTISER_LINKS = [
  { label: 'Advertise', to: '/advertise' },
];

export default function Nav({ dark = true }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { isSignedIn, openDashboard, openingDashboard } = useOpenDashboard();
  const isMarketing = !location.pathname.startsWith('/user') &&
    !location.pathname.startsWith('/advertiserDashboard') &&
    !location.pathname.startsWith('/adminDashboard');

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  if (!isMarketing) return null;

  const textClass = dark ? 'text-white' : 'text-invert-navy';
  const mutedClass = dark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-invert-navy';
  const navBg = dark
    ? 'bg-invert-navy/95 backdrop-blur-xl border-b border-white/5'
    : 'bg-white/95 backdrop-blur-xl border-b border-invert-border';
  const mobileMenuBg = dark ? 'bg-invert-navy border-white/10' : 'bg-white border-invert-border';
  const mobileMenuBorder = dark ? 'border-white/10' : 'border-invert-border';
  const mobileLoginVariant = dark ? 'secondary' : 'outline';
  const mobileDashboardVariant = dark ? 'primary' : 'secondaryDark';

  return (
    <header className={`fixed top-0 left-0 right-0 z-[1000] ${navBg} pt-[env(safe-area-inset-top)]`}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 lg:h-20 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 shrink-0 min-w-0">
          <img src={dark ? logoWhite : logo} alt="INVERT" className="h-7 sm:h-8 lg:h-9 w-auto" />
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium transition-colors ${
                location.pathname === link.to ? textClass : mutedClass
              }`}
            >
              {link.label}
            </Link>
          ))}
          <span className="w-px h-4 bg-white/20" />
          {ADVERTISER_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium transition-colors ${mutedClass}`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          {isSignedIn ? (
            <Button
              variant={dark ? 'primary' : 'secondaryDark'}
              size="sm"
              onClick={openDashboard}
              disabled={openingDashboard}
            >
              {openingDashboard ? 'Opening…' : 'Open Dashboard'}
            </Button>
          ) : (
            <>
              <Button to="/screenHostLogin/" variant="ghost" size="sm" className={mutedClass}>
                Log in
              </Button>
              <Button to="/screenHostSignup" variant={dark ? 'primary' : 'secondaryDark'} size="sm">
                Get Started
              </Button>
            </>
          )}
        </div>

        <div className="flex lg:hidden items-center gap-2 shrink-0">
          {isSignedIn ? (
            <Button
              variant={mobileDashboardVariant}
              size="sm"
              className="min-h-[40px] px-3.5 whitespace-nowrap"
              onClick={openDashboard}
              disabled={openingDashboard}
            >
              {openingDashboard ? 'Opening…' : 'Dashboard'}
            </Button>
          ) : (
            <Button
              to="/screenHostLogin/"
              variant={mobileLoginVariant}
              size="sm"
              className="min-h-[40px] px-3.5 whitespace-nowrap font-semibold"
            >
              Log in
            </Button>
          )}
          <button
            type="button"
            className={`min-h-[40px] min-w-[40px] flex items-center justify-center rounded-lg ${textClass} ${dark ? 'hover:bg-white/10' : 'hover:bg-invert-navy/5'}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <>
          <button
            type="button"
            className="lg:hidden fixed inset-0 top-[calc(3.5rem+env(safe-area-inset-top))] sm:top-[calc(4rem+env(safe-area-inset-top))] bg-black/40 z-[999]"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
          />
          <div className={`lg:hidden relative z-[1001] border-t ${mobileMenuBg} max-h-[calc(100dvh-3.5rem-env(safe-area-inset-top))] sm:max-h-[calc(100dvh-4rem-env(safe-area-inset-top))] overflow-y-auto`}>
            <div className="px-4 sm:px-6 py-4 flex flex-col gap-1 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              {[...NAV_LINKS, ...ADVERTISER_LINKS].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`py-3.5 px-1 text-base font-medium min-h-[44px] flex items-center ${textClass} ${dark ? 'active:bg-white/5' : 'active:bg-invert-navy/5'} rounded-lg`}
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className={`pt-4 flex flex-col gap-3 border-t ${mobileMenuBorder} mt-2`}>
                {isSignedIn ? (
                  <Button
                    variant="primary"
                    size="md"
                    className="w-full min-h-[48px]"
                    onClick={(event) => {
                      setMobileOpen(false);
                      openDashboard(event);
                    }}
                    disabled={openingDashboard}
                  >
                    {openingDashboard ? 'Opening…' : 'Open Dashboard'}
                  </Button>
                ) : (
                  <>
                    <Button
                      to="/screenHostSignup"
                      variant="primary"
                      size="md"
                      className="w-full min-h-[48px]"
                      onClick={() => setMobileOpen(false)}
                    >
                      Get Started — Free
                    </Button>
                    <Button
                      to="/screenHostLogin/"
                      variant={mobileLoginVariant}
                      size="md"
                      className="w-full min-h-[48px]"
                      onClick={() => setMobileOpen(false)}
                    >
                      Log in to your account
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
}
